import { useState } from "react";
import { toast } from "sonner";
import { Handshake, Newspaper, Medal } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import { Reveal, Eyebrow } from "@/components/site/ui";

const CONTACT_TYPES = [
  { key: "Partnership", label: "Partnership Inquiry", icon: Handshake },
  { key: "Media", label: "Media Request", icon: Newspaper },
  { key: "Competition", label: "Competition Inquiry", icon: Medal },
];

function CField({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="label-eyebrow">{label}</label>
      <input type={type} data-testid={testid} value={value} onChange={onChange} required={required} className="mt-2 w-full rounded-2xl bg-white border border-champagne/40 px-5 py-3.5 font-body text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-bronze" />
    </div>
  );
}

export function Contact() {
  const [type, setType] = useState("Partnership");
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/inquiries", { ...form, inquiry_type: type });
      toast.success(data.message);
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-gradient-to-b from-ivory to-blush/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><Eyebrow>Contact</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 className="mt-4 font-heading text-4xl md:text-6xl text-ink">Let's Connect.</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-5 font-body text-base md:text-lg text-ink/70">For partnership, media, competition, or speaking opportunities.</p></Reveal>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2">
            <div className="rounded-[2rem] bg-ink text-ivory p-8 md:p-10 h-full flex flex-col">
              <div className="mb-7 overflow-hidden rounded-[1.5rem] aspect-[4/5]"><img src={IMAGES.headshotStudio} alt="Leah Thames" className="w-full h-full object-cover object-top" /></div>
              <Eyebrow className="!text-champagne">Let's Work Together</Eyebrow>
              <p className="mt-3 font-heading text-2xl md:text-3xl leading-tight">Every great partnership starts with a conversation.</p>
              <p className="mt-4 font-body text-sm text-ivory/75 leading-relaxed">Whether it's a brand collaboration, media feature, or competition opportunity, share a few details and the right person will be in touch.</p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="rounded-[2rem] bg-ivory border border-champagne/30 p-8 md:p-10">
              <div className="flex flex-wrap gap-3">
                {CONTACT_TYPES.map((t) => (
                  <button type="button" key={t.key} onClick={() => setType(t.key)} data-testid={`contact-type-${t.key.toLowerCase()}`} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-body text-sm border transition-colors ${type === t.key ? "bg-blush text-ink border-bronze" : "bg-ivory text-ink/70 border-champagne/40 hover:border-bronze"}`}><t.icon size={15} /> {t.label}</button>
                ))}
              </div>
              <div className="mt-7 grid sm:grid-cols-2 gap-5">
                <CField label="Name" testid="contact-name" value={form.name} onChange={update("name")} required />
                <CField label="Email" testid="contact-email" type="email" value={form.email} onChange={update("email")} required />
              </div>
              <div className="mt-5"><CField label="Organization (optional)" testid="contact-organization" value={form.organization} onChange={update("organization")} /></div>
              <div className="mt-5">
                <label className="label-eyebrow">Message</label>
                <textarea data-testid="contact-message" value={form.message} onChange={update("message")} required rows={5} placeholder="Tell us a little about the opportunity…" className="mt-2 w-full rounded-2xl bg-white border border-champagne/40 px-5 py-4 font-body text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-bronze resize-none" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="mt-7 w-full rounded-full bg-bronze px-8 py-4 font-body text-sm font-semibold text-ivory hover:bg-ink transition-colors disabled:opacity-60">{loading ? "Sending…" : `Send ${type} Inquiry`}</button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
